import DataLoader from 'dataloader'
import { GeneralError } from 'fejl'
import { orderBy } from 'lodash'
import memoize from 'memoizee'
import { runInAction } from 'mobx'
import { Observable, race, firstValueFrom, Subject } from 'rxjs'
import { filter, first, map, switchMap } from 'rxjs/operators'
import { PrimeMemberByPublicId } from 'iam/MemberStore'
import Member from 'iam/models/Member'
import User from 'iam/models/User'
import { GetCurrentMember } from 'iam/SessionStore'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { CancellationToken } from 'infra/cancellation'
import { collection } from 'infra/collection'
import { DocumentDownloadDelegate } from 'shell/platform/types'
import { CursorPaged, Direction, Id, PrimeProps } from 'types/definitions'
import InfiniteScrollProvider, {
  FetchItems,
} from 'utils/InfiniteScrollProvider'
import { tapAction, toStream } from 'utils/mobx-util'
import {
  DocumentCreatedV1Event,
  DocumentUnlinkedV1Event,
  DocumentUpdatedV1Event,
  DocumentV1Payload,
  ManualVerificationRequiredV1Event,
  ZipCreatedV1Event,
  ZipFailedV1Event,
} from '@taxfyle/documents-api-client'
import { DocumentResource } from '@taxfyle/documents-api-client/v5'
import { getCacheKeyForObject, isNotNil } from 'utils/object-util'
import { DocumentAPI, DocumentsApiV5 } from './API'
import Document from './Document'
import {
  mapFileDescriptorToDocumentStage,
  mapSearchQueryToFindDocumentsQuery,
  mapSearchQueryToSearchDocumentsQuery,
  mapUploadRequestToCreateDocumentPayload,
} from './document-mappers'
import {
  mapDocumentResourceToProps,
  mapDocumentV1PayloadToProps,
  mapSearchDocumentsQueryToSearchRequest,
} from './document-mappers-v5'
import isDocumentsApiV5Enabled from './documentsApiV5FeatureToggle'
import { GetJob } from 'jobs/JobStore'
import {
  PrimeDocumentType,
  PrimeDocumentTypeByShortId,
} from './DocumentTypeStore'
import { DocumentDTO, DocumentPopulate, ZipFilesPayload } from './dtos/document'
import { CreateDocumentVersionPayload } from './dtos/document-version'
import {
  RealtimeMessage,
  ZipCreatedMessage,
  ZipFailedMessage,
} from './dtos/realtime-messages'
import { v7 } from 'uuid'

/**
 * The realtime messages the documents connection can carry — the
 * legacy events and the v1 events are broadcast on the same socket.
 */
type DocumentsRealtimeMessage =
  | RealtimeMessage
  | DocumentCreatedV1Event
  | DocumentUpdatedV1Event
  | ManualVerificationRequiredV1Event
  | DocumentUnlinkedV1Event
  | ZipCreatedV1Event
  | ZipFailedV1Event

export type GetDocument = (id: Id) => Document | undefined

export type PrimeDocument = (
  props: PrimeProps<DocumentDTO, 'long_id'>,
) => Document | undefined

export interface UploadDocumentOpts {
  /**
   * Callback invoked when the document is created.
   */
  onDocumentCreated?: (document: Document) => void
  /**
   * When already cancelled by the time the document is created, the
   * document is discarded instead of uploaded.
   */
  cancellationToken?: CancellationToken
}

/**
 * The fields the documents can be sorted by.
 */
export type DocumentsSortField = 'title' | 'date_created' | 'date_modified'

/**
 * The filters shared by every search context.
 */
interface SearchDocumentsQueryBase {
  limit?: number
  after?: string | null
  archived?: boolean
  orderBy?: {
    field: DocumentsSortField
    direction: Direction
  }
}

/**
 * Searches the documents linked to the given job.
 */
export interface JobDocumentsQuery extends SearchDocumentsQueryBase {
  context: 'Job'
  jobId: Id
  workspaceId?: Id
}

/**
 * Searches the documents owned by the current user.
 */
export interface UserDocumentsQuery extends SearchDocumentsQueryBase {
  context: 'User'
  workspaceId?: Id
}

/**
 * Searches documents across workspaces as an admin.
 */
export interface AdminDocumentsQuery extends SearchDocumentsQueryBase {
  context: 'Admin'
  searchText?: string
  ownerPublicIds?: string[]
  workspaceIds?: string[]
  jobIds?: string[]
  documentTypeIds?: string[]
}

/**
 * Query for searching documents. Owned by the document store — the
 * mappers translate it to the legacy or v5 API request shapes.
 */
export type SearchDocumentsQuery =
  | JobDocumentsQuery
  | UserDocumentsQuery
  | AdminDocumentsQuery

/**
 * Request for assigning a type to a document.
 */
export interface AssignDocumentTypeRequest {
  /**
   * The document type's short id.
   */
  documentTypeId: string
}

/**
 * Request for updating a document.
 */
export interface UpdateDocumentRequest {
  title?: string
  description?: string
}

/**
 * Request for uploading a document.
 */
export interface UploadDocumentRequest {
  file: File
  workspaceId: Id
  documentTypeId?: string
  /**
   * The job to link the document to. The v5 API requires a link, so
   * linkless uploads stay on the legacy API.
   */
  jobId?: Id
}

const orderDocuments = (docs: Array<Document>) =>
  orderBy(docs, 'dateCreated', 'desc')

export default class DocumentStore {
  /**
   * The realtime events on the legacy API's connection.
   */
  private realtimeEventsLegacy$ =
    this.api.documents.events<DocumentsRealtimeMessage>()

  /**
   * The realtime events on the v5 API's connection.
   */
  private realtimeEventsV5$ = this.apiV5.events<DocumentsRealtimeMessage>()

  /**
   * The manual verification required subject.
   */
  private manualVerificationRequiredSubject = new Subject<Document>()

  documents = collection<DocumentDTO, Document>({
    getDataId: (data) => data.long_id,
    getModelId: (model) => model.id,
    create: () =>
      new Document(
        this.primeMemberByPublicId,
        this.getCurrentMember,
        this.primeDocumentType,
        this.primeWorkspace,
        this.getJob,
      ),
  })

  /**
   * Emits the document whenever manual verification becomes required for it.
   * The document is refreshed first so its stage reflects the new state.
   */
  manualVerificationRequired$ =
    this.manualVerificationRequiredSubject.asObservable()

  /**
   * Scroll providers.
   */
  scrollProviders = new Map<
    string,
    InfiniteScrollProvider<Document, Record<string, unknown>>
  >()

  /**
   * Documents loader.
   */
  documentsLoader = new DataLoader(this._fetchDocumentsByIds.bind(this), {
    cacheKeyFn: getCacheKeyForObject,
  })

  constructor(
    private api: DocumentAPI,
    private apiV5: DocumentsApiV5,
    private primeMemberByPublicId: PrimeMemberByPublicId,
    private getCurrentMember: GetCurrentMember,
    private primeDocumentType: PrimeDocumentType,
    private primeDocumentTypeByShortId: PrimeDocumentTypeByShortId,
    private primeWorkspace: PrimeWorkspace,
    private getJob: GetJob,
    private getDocumentDownloadDelegate: () => DocumentDownloadDelegate,
    currentMember$: Observable<Member>,
  ) {
    this.primeDocument = memoize(this.primeDocument, {
      promise: true,
      length: 1,
    })

    toStream(() => isDocumentsApiV5Enabled(), true)
      .pipe(
        switchMap((useApiV5) =>
          useApiV5
            ? this.realtimeEventsV5$.pipe(
                tapAction((e) => this._handleRealtimeEventV5(e)),
              )
            : this.realtimeEventsLegacy$.pipe(
                tapAction((e) => this._handleRealtimeEventLegacy(e)),
              ),
        ),
      )
      .subscribe()

    currentMember$.subscribe(() => {
      // this.documents.clear()
      this.scrollProviders.clear()
    })
  }

  /**
   * Scroll provider for the workspace member in the specified workspace.
   * @param workspaceId
   */
  userDocumentsScrollProviderForWorkspace(workspaceId: Id) {
    workspaceId = Id.assert(workspaceId)
    return this.getScrollProvider(`workspace-${workspaceId}`, (params) =>
      this.fetchUserDocuments({
        ...params,
        workspaceId,
      }),
    )
  }

  /**
   * Gets or inits a scroll provider.
   * A scroll provider is used to manage infinite scrolling state
   * for APIs using cursor-based paging.
   */
  getScrollProvider(uid: string, fetchItems: FetchItems<Document>) {
    const existing = this.scrollProviders.get(uid)
    if (existing) {
      return existing
    }

    const newProvider = new InfiniteScrollProvider({
      fetchItems,
      memoizeInitial: true,
      limit: 30,
    })

    this.scrollProviders.set(uid, newProvider)
    return newProvider
  }

  /**
   * Returns the documents for the given job ID from the collection.
   */
  forJob(jobId: string) {
    return orderDocuments(
      this.documents.filter((doc) => {
        const link = doc.links.job.jobIds.find((id) => id === jobId)
        return !!link && !doc.ephemeral
      }),
    )
  }

  /**
   * Returns the documents for the given member from the collection.
   */
  forMember(member: Member) {
    return orderDocuments(
      this.documents.filter((doc) => doc.owner === member && !doc.ephemeral),
    )
  }

  /**
   * Returns the non-archived documents for the given user from the
   * collection.
   */
  forUser(user: User) {
    return orderDocuments(
      this.documents.filter(
        (doc) => doc.owner.user === user && !doc.archived && !doc.ephemeral,
      ),
    )
  }

  /**
   * Primes a document in the collection.
   */
  primeDocument: PrimeDocument = (props) => {
    if (!props?.long_id) {
      return undefined
    }

    let doc = this.documents.get(props.long_id)

    if (!doc) {
      doc = this.documents.create(props)
    }

    // Fetch the doc in the background, including it's related information.
    void this.refreshDocument(props.long_id)

    return doc
  }

  /**
   * Fetches the documents for the current member.
   */
  async fetchUserDocuments(params: Omit<UserDocumentsQuery, 'context'>) {
    return this.searchDocuments({
      context: 'User',
      workspaceId: Id.assert(this.getCurrentMember().workspace.id),
      ...params,
    })
  }

  /**
   * Fetches the document given a ID.
   */
  async fetchDocument(id: Id, populate?: DocumentPopulate) {
    return this.documentsLoader.load([Id.assert(id), { ...populate }])
  }

  /**
   * Searches the documents given the query.
   */
  async searchDocuments(
    query: SearchDocumentsQuery,
  ): Promise<CursorPaged<Document>> {
    if (isDocumentsApiV5Enabled()) {
      return this._searchDocumentsV5(query)
    }

    return query.context === 'Admin'
      ? this._searchDocumentsLegacy(query)
      : this._findDocumentsLegacy(query)
  }

  /**
   * Finds the documents using the legacy API.
   */
  private async _findDocumentsLegacy(
    query: JobDocumentsQuery | UserDocumentsQuery,
  ) {
    const paged = await this.api.documents.find({
      ...mapSearchQueryToFindDocumentsQuery(query),
      accesses: true,
      history: true,
    })
    return { ...paged, data: this.documents.set(paged.data) }
  }

  /**
   * Searches the documents using the legacy API.
   */
  private async _searchDocumentsLegacy(query: AdminDocumentsQuery) {
    return this.api.documents
      .search(mapSearchQueryToSearchDocumentsQuery(query))
      .then((paged) => ({
        ...paged,
        data: this.documents.set(paged.data),
      }))
  }

  /**
   * Searches the documents using the v5 API.
   */
  private async _searchDocumentsV5(query: SearchDocumentsQuery) {
    const response = await this.apiV5.documents.search({
      query: mapSearchDocumentsQueryToSearchRequest(query),
    })

    return {
      data: response.data.map((resource) => this._setDocumentV5(resource)),
      cursor: response.cursor,
      total: response.total,
    }
  }

  /**
   * Uploads a document.
   */
  async uploadDocument(
    request: UploadDocumentRequest,
    opts?: UploadDocumentOpts,
  ): Promise<Document> {
    return isDocumentsApiV5Enabled() && request.jobId
      ? this._uploadDocumentV5(request, opts)
      : this._uploadDocumentLegacy(request, opts)
  }

  /**
   * Uploads a document using the v5 API.
   */
  private async _uploadDocumentV5(
    request: UploadDocumentRequest,
    opts?: UploadDocumentOpts,
  ): Promise<Document> {
    const documentId = Id.assert(v7())

    const document = runInAction(() => {
      const document = this.documents.set({
        long_id: documentId,
        title: request.file.name,
        workspace_id: request.workspaceId,
      })

      const now = new Date()

      return document.set({
        uploadProgress: {
          total: 0,
          percent: 0,
          uploaded: 0,
        },
        dateCreated: now,
        dateModified: now,
        owner: this.getCurrentMember(request.workspaceId),
        links: {
          job: { jobIds: [Id.assert(request.jobId)] },
          workRequest: { workRequestId: null },
          strategy: { folderId: null },
        },
      })
    })

    if (opts?.cancellationToken?.isCancelled) {
      this.documents.remove(documentId)
      return document
    }

    await this.apiV5.documents
      .upload(
        {
          document_id: documentId,
          workspace_id: Id.assert(request.workspaceId),
          document_type_id: request.documentTypeId,
          link: JSON.stringify({
            type: 'JOB',
            job_id: Id.assert(request.jobId),
          }),
          file: [request.file],
        },
        {
          onProgress: (e) => {
            document.set({
              uploadProgress: {
                total: e.total,
                uploaded: e.loaded,
                percent: Math.ceil((e.loaded / e.total) * 100),
              },
            })
          },
        },
      )
      .catch((err) => {
        runInAction(() => {
          document.set({ uploadProgress: null })
          this.documents.remove(documentId)
        })
        throw err
      })

    document.set({ uploadProgress: null })

    opts?.onDocumentCreated?.(document)

    return (await this.refreshDocument(documentId)) ?? document
  }

  /**
   * Uploads a document using the legacy API, by creating the document
   * and its first version as separate calls.
   */
  private async _uploadDocumentLegacy(
    request: UploadDocumentRequest,
    opts?: UploadDocumentOpts,
  ): Promise<Document> {
    const documentDto = await this.api.documents.create(
      mapUploadRequestToCreateDocumentPayload(request),
    )

    // Set the uploadProgress before adding it to the collection to avoid
    // showing the file as corrupted while the version is being created.
    const document = this.documents.create(documentDto).set({
      uploadProgress: {
        total: 0,
        percent: 0,
        uploaded: 0,
      },
    })

    this.documents.add(document)

    const id = Id.assert(document.id)

    if (opts?.cancellationToken?.isCancelled) {
      // Archives the document.
      await this.archiveDocument(id)
      return document
    }

    opts?.onDocumentCreated?.(document)

    return this._uploadVersionLegacy({
      document_long_id: id,
      message: 'Initial version',
      action: { type: 'ReplaceContents', files: [request.file] },
    })
  }

  /**
   * Updates a document. The v5 API has no update endpoint, so this
   * always uses the legacy API.
   *
   * TODO: will be removed when v5 is canonical.
   */
  async updateDocument(id: string, request: UpdateDocumentRequest) {
    return this._updateDocumentLegacy(id, request)
  }

  /**
   * Updates a document using the legacy API.
   */
  private async _updateDocumentLegacy(
    id: string,
    request: UpdateDocumentRequest,
  ) {
    const updated = await this.api.documents.update(id, {
      title: request.title,
      description: request.description,
    })
    return this.documents.set(updated)
  }

  /**
   * Archives a document.
   */
  async archiveDocument(id: string) {
    return isDocumentsApiV5Enabled()
      ? this._archiveDocumentV5(id)
      : this._archiveDocumentLegacy(id)
  }

  /**
   * Archives a document using the v5 API. The document is refetched so
   * its retention reflects the archival.
   */
  private async _archiveDocumentV5(id: string) {
    await this.apiV5.documents.archive({ id: Id.assert(id) })
    return this.refreshDocument(Id.assert(id))
  }

  /**
   * Archives a document using the legacy API. The archive response
   * doesn't include accesses, so the document is refetched instead.
   */
  private async _archiveDocumentLegacy(id: string) {
    await this.api.documents.archive(id)
    return this.refreshDocument(Id.assert(id))
  }

  /**
   * Assings document type.
   */
  async assignType(id: string, request: AssignDocumentTypeRequest) {
    return isDocumentsApiV5Enabled()
      ? this._assignTypeV5(id, request)
      : this._assignTypeLegacy(id, request)
  }

  /**
   * Assigns the document type using the v5 API. The response is empty,
   * so the document is refetched.
   */
  private async _assignTypeV5(id: string, request: AssignDocumentTypeRequest) {
    await this.apiV5.documents.assignType({
      id: Id.assert(id),
      payload: { document_type_id: request.documentTypeId },
    })

    return this.refreshDocument(Id.assert(id))
  }

  /**
   * Assigns the document type using the legacy API.
   */
  private async _assignTypeLegacy(
    id: string,
    request: AssignDocumentTypeRequest,
  ) {
    await this.api.documents.assignType(id, { type: request.documentTypeId })

    return this.refreshDocument(Id.assert(id))
  }

  /**
   * Downloads all the specified documents as a zip file.
   */
  async downloadAll(ids: Id[]) {
    return isDocumentsApiV5Enabled()
      ? this._downloadAllV5(ids)
      : this._downloadAllLegacy(ids)
  }

  /**
   * Downloads all the specified documents as a zip file using the
   * v5 API. The zip is built in the background and its signed URL
   * arrives on the realtime connection.
   */
  private async _downloadAllV5(ids: Id[]) {
    const { work_id } = await this.apiV5.documents.zipFiles({
      payload: { document_ids: ids.map((id) => Id.assert(id)) },
    })

    // Wait for the success event
    const created$ = this.realtimeEventsV5$.pipe(
      filter(
        (e): e is ZipCreatedV1Event => e.type === 'documents.v1.zip-created',
      ),
      first((e) => e.data.zip_request_id === work_id),
      map((e) => e.data.url),
    )

    // Throw when we get an error
    const failed$ = this.realtimeEventsV5$.pipe(
      filter(
        (e): e is ZipFailedV1Event => e.type === 'documents.v1.zip-failed',
      ),
      first((e) => e.data.zip_request_id === work_id),
      map((e): string => {
        throw new GeneralError(e.data.message)
      }),
    )

    const url = await firstValueFrom(race(created$, failed$))
    this.getDocumentDownloadDelegate().download(url, false)
  }

  /**
   * Downloads all the specified documents as a zip file using the
   * legacy API, resolving each document's version and file ids first.
   */
  private async _downloadAllLegacy(ids: Id[]) {
    const docs = (
      await Promise.all(
        ids.map(async (id) => {
          const dto = await this.api.documents.get(Id.assert(id), {
            history: true,
          })

          return dto
            ? {
                document_id: dto.id,
                file_id: dto.latest_version.files[0].id,
                version_id: dto.latest_version.id,
              }
            : null
        }),
      )
    ).filter(isNotNil) satisfies ZipFilesPayload

    const ephemeralDto = await this.api.documents.zipFiles(docs)

    // Wait for the success message
    const created$ = this.api.documents
      .when<ZipCreatedMessage>('ZipCreated')
      .pipe(first((msg) => msg.data.document_id === ephemeralDto.id))

    // Throw when we get an error
    const failed$ = this.api.documents.when<ZipFailedMessage>('ZipFailed').pipe(
      first((msg) => msg.data.document_id === ephemeralDto.id),
      map((msg) => {
        throw new GeneralError(msg.data.reason)
      }),
    )

    return firstValueFrom(
      race(created$, failed$).pipe(
        switchMap(() =>
          this.fetchDocument(ephemeralDto.long_id, {
            accesses: true,
            history: true,
          }),
        ),
        switchMap(async (doc) => this.download(doc, false)),
      ),
    )
  }

  /**
   * Downloads the document via a signed URL handed to the platform delegate.
   */
  download(document: Document, preview: boolean) {
    if (isDocumentsApiV5Enabled()) {
      this._downloadV5(document, preview)
    } else {
      this._downloadLegacy(document, preview)
    }
  }

  /**
   * Downloads the document using the v5 API.
   */
  private _downloadV5(document: Document, preview: boolean) {
    const downloadDelegate = this.getDocumentDownloadDelegate()

    void this.apiV5.documents
      .download({ id: Id.assert(document.id), preview })
      .then((r) => downloadDelegate.download(r.url, preview))
  }

  /**
   * Downloads the document using the legacy API.
   */
  private _downloadLegacy(document: Document, preview: boolean) {
    const downloadDelegate = this.getDocumentDownloadDelegate()

    void this.api.documents
      .getDownloadUrl(Id.assert(document.id), preview)
      .then((r) => downloadDelegate.download(r.url, preview))
  }

  /**
   * Refetches the document, bypassing the documents loader cache,
   * and returns the document from the collection.
   */
  async refreshDocument(id: Id) {
    await this._fetchDocumentsByIds([
      [Id.assert(id), { accesses: true, history: true }],
    ])
    return this.documents.get(id)
  }

  /**
   * Links the document to the specified job.
   */
  async addLink(document: Document, jobId: Id) {
    return isDocumentsApiV5Enabled()
      ? this._addLinkV5(document, jobId)
      : this._addLinkLegacy(document, jobId)
  }

  /**
   * Links the document to the specified job using the v5 API.
   */
  private async _addLinkV5(document: Document, jobId: Id) {
    await this.apiV5.documents.link({
      id: Id.assert(document.id),
      payload: { type: 'JOB', job_id: Id.assert(jobId) },
    })

    await this.refreshDocument(document.id)
  }

  /**
   * Links the document to the specified job using the legacy API.
   */
  private async _addLinkLegacy(document: Document, jobId: Id) {
    await this.api.access.create({
      document_id: Id.assert(document.id),
      access: { type: 'Job', job_id: Id.assert(jobId) },
    })

    await this.refreshDocument(document.id)
  }

  /**
   * Unlinks the document from the specified job.
   */
  async removeLink(document: Document, jobId: Id) {
    if (!document.isLinkedToJob(jobId)) {
      throw new Error(
        'Cannot unlink the document because the link was not found',
      )
    }

    return isDocumentsApiV5Enabled()
      ? this._removeLinkV5(document, jobId)
      : this._removeLinkLegacy(document, jobId)
  }

  /**
   * Unlinks the document from the specified job using the v5 API.
   */
  private async _removeLinkV5(document: Document, jobId: Id) {
    await this.apiV5.documents.unlink({
      id: Id.assert(document.id),
      payload: { type: 'JOB', job_id: Id.assert(jobId) },
    })

    await this.refreshDocument(document.id)
  }

  /**
   * Unlinks the document from the specified job using the legacy API.
   * Links only carry job ids, so the backing access id is resolved by
   * refetching the accesses.
   */
  private async _removeLinkLegacy(document: Document, jobId: Id) {
    const dto = await this.api.documents.get(Id.assert(document.id), {
      accesses: true,
    })
    const access = dto?.accesses?.find(
      (a) => a.type === 'Job' && a.job_id === jobId,
    )

    if (!access) {
      throw new Error(
        'Cannot unlink the document because the link was not found',
      )
    }

    await this.api.access.delete(access.id)
    await this.refreshDocument(document.id)
  }

  /**
   * Confirms document for the given job.
   */
  async confirmDocument(documentLongId: Id, jobId: Id) {
    return isDocumentsApiV5Enabled()
      ? this._confirmDocumentV5(documentLongId)
      : this._confirmDocumentLegacy(documentLongId, jobId)
  }

  /**
   * Confirms the document using the v5 API, which attests it by its
   * id alone.
   */
  private async _confirmDocumentV5(documentLongId: Id) {
    await this.apiV5.documents.attest({ id: Id.assert(documentLongId) })
    await this.refreshDocument(documentLongId)
  }

  /**
   * Confirms the document using the legacy API. The latest version and
   * file ids are resolved by refetching since the client doesn't track
   * versions.
   */
  private async _confirmDocumentLegacy(documentLongId: Id, jobId: Id) {
    const dto = await this.api.documents.get(Id.assert(documentLongId), {
      history: true,
    })

    const latestVersion = dto?.latest_version
    const fileId = latestVersion?.files[0]?.id

    if (!latestVersion || !fileId) {
      throw new Error(
        'Cannot confirm the document because its latest version could not be resolved',
      )
    }

    await this.api.clientDataVerification.confirm({
      document_long_id: Id.assert(documentLongId),
      version_id: Id.assert(latestVersion.id),
      file_id: Id.assert(fileId),
      job_id: Id.assert(jobId),
    })

    await this.refreshDocument(documentLongId)
  }

  /**
   * Fetches the documents given the ids.
   */
  private async _fetchDocumentsByIds(
    ids: readonly [string, DocumentPopulate][],
  ) {
    return isDocumentsApiV5Enabled()
      ? this._fetchDocumentsByIdsV5(ids)
      : this._fetchDocumentsByIdsLegacy(ids)
  }

  /**
   * Fetches the documents given the ids using the v5 API.
   */
  private async _fetchDocumentsByIdsV5(
    ids: readonly [string, DocumentPopulate][],
  ) {
    return Promise.all(
      ids.map(([id]) =>
        this.apiV5.documents
          .get({ id })
          .then((resource) => this._setDocumentV5(resource)),
      ),
    )
  }

  /**
   * Sets a document resource into the collection and patches
   * the document's props from it.
   */
  private _setDocumentV5(resource: DocumentResource) {
    const document = this.documents.set({ long_id: Id.assert(resource.id) })

    return document.set(
      mapDocumentResourceToProps(
        resource,
        this.primeMemberByPublicId,
        this.primeWorkspace,
        this.primeDocumentTypeByShortId,
      ),
    )
  }

  /**
   * Fetches the documents given the ids using the legacy API.
   */
  private async _fetchDocumentsByIdsLegacy(
    ids: readonly [string, DocumentPopulate][],
  ) {
    return Promise.all(
      ids.map(([id, populate]) =>
        this.api.documents.get(id, { ...populate }).then(this.documents.set),
      ),
    )
  }

  /**
   * Handles the v1 realtime events emitted by the v5 API.
   */
  private _handleRealtimeEventV5(e: DocumentsRealtimeMessage) {
    switch (e.type) {
      case 'documents.v1.created':
      case 'documents.v1.updated':
        // Updates documents that are still in the collection.
        if (!e.data.archived || this.documents.get(e.data.id)) {
          const document = this._setDocumentV1(e.data)

          // Emits only when the document is not archived.
          if (!document.archived && document.isManualAttestationRequired) {
            this.manualVerificationRequiredSubject.next(document)
          }
        }

        break

      case 'documents.v1.unlinked': {
        const link = e.data.link
        if (link.type !== 'JOB') {
          break
        }

        const document = this.documents.get(e.data.id)
        document?.set({
          links: {
            ...document.links,
            job: {
              jobIds: document.links.job.jobIds.filter(
                (id) => id !== link.job_id,
              ),
            },
          },
        })
        break
      }
    }
  }

  /**
   * Materializes a v1 realtime document payload into the collection and
   * patches the document's props from it.
   */
  private _setDocumentV1(payload: DocumentV1Payload) {
    const document = this.documents.set({ long_id: Id.assert(payload.id) })

    return document.set(
      mapDocumentV1PayloadToProps(
        payload,
        this.primeMemberByPublicId,
        this.primeWorkspace,
        this.primeDocumentTypeByShortId,
      ),
    )
  }

  /**
   * Handles realtime events emitted by the legacy API.
   */
  private _handleRealtimeEventLegacy(e: DocumentsRealtimeMessage) {
    switch (e.type) {
      case 'DocumentCreated':
      case 'DocumentUpdated':
        // Archived updates only patch documents still in the collection,
        // so locally discarded documents don't get resurrected.
        if (!e.data.archived || this.documents.get(e.data.long_id)) {
          this.documents.set(e.data)
        }
        break

      // The document's stage is derived from its latest version's file
      // state, so version events must patch it on the document.
      case 'VersionScanned':
      case 'ClientDataVerified':
        this.documents.get(e.data.document_long_id)?.set({
          stage: mapFileDescriptorToDocumentStage(e.data.files[0]),
        })
        break
      case 'ClientDataManualVerificationRequired': {
        const document = this.documents.get(e.data.document_long_id)
        document?.set({
          stage: mapFileDescriptorToDocumentStage(e.data.files[0]),
        })

        // emits only when the document is not archived.
        if (document && !document.archived) {
          this.manualVerificationRequiredSubject.next(document)
        }
        break
      }

      // The document's links and edit rights are derived from its
      // accesses, so access events must refresh the document.
      case 'AccessCreated':
      case 'AccessUpdated':
      case 'AccessDeleted':
        void this.refreshDocument(e.data.document_long_id)
        break
    }
  }

  /**
   * Uploads a new version for the document using the legacy API.
   */
  private async _uploadVersionLegacy(
    data: CreateDocumentVersionPayload,
  ): Promise<Document> {
    const document = this.documents.get(data.document_long_id)
    if (!document) {
      throw new Error(
        'Cannot upload a version because the document was not found in the collection',
      )
    }

    const result = await this.api.version
      .create(data, {
        onProgress: (e) => {
          document.set({
            uploadProgress: {
              total: e.total,
              uploaded: e.loaded,
              percent: Math.ceil((e.loaded / e.total) * 100),
            },
          })
        },
      })
      .catch((err) => {
        document.set({ uploadProgress: null })
        throw err
      })

    return runInAction(() => {
      document.set({
        uploadProgress: null,
        stage: mapFileDescriptorToDocumentStage(result.files[0]),
      })
      return document
    })
  }
}
